// src/routes/MainRoutes.js

import React, { lazy, Suspense } from 'react';
import { Route, Switch, Redirect, useLocation, useHistory } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { performLogout } from '../utils/logout';

import Loadable from '../ui-component/Loadable';
import InsightHubLayout from '../insight-hub/InsightHubLayout';
import InsightHubShell from '../insight-hub/InsightHubShell';

// Core
import AccessDenied from '../views/errors/AccessDenied';
import Dashboard1 from '../components/Dashboard1';
import DashboardDefault from '../views/dashboard/Default';

// Static Utilities
import SettingsPage from '../views/utilities/Settingspage';
import UserProfile from '../views/utilities/UserProfile';
import ForgotPasswordPage from '../views/utilities/ForgotPasswordPage';
import DataManagement from '../views/utilities/DataManagement';
import ManageExcelUpload from '../views/utilities/ManageExcelUpload';
import ManageRecords from '../views/utilities/ManageRecords';
import ReportDesign from '../views/utilities/ReportDesign';
import DashboardReportAllocationPage from '../views/utilities/DashboardReportAllocationPage';
import OrgStats from '../views/utilities/OrgStats';
import ChartOfAccounts from '../views/utilities/SubPages/MonthWise';
import CummlativeKmRun from '../views/utilities/SubPages/CummlativeKmRun';
import Master from '../views/utilities/Master';
import CasingDamageReport from '../views/utilities/CasingDamageReport';
import AccessAndManageReports from '../views/utilities/AccessAndManageReports';
import ManageSubscriptions from '../views/utilities/ManageSubscriptions';
import ManageUsers from '../views/utilities/ManageUsers';
import ReportsAllocation from '../views/utilities/ReportAllocation';
import PowerBI from '../views/utilities/PowerBI';
import BillOfMaterial from '../views/utilities/BillOfMaterial';
import MaterialMasterUpload from '../views/utilities/MaterialMasterUpload';
import UploadFile from '../views/utilities/UploadFile';
import Reports from '../views/utilities/Reports';
import PassiveTyres from '../views/utilities/PassiveTyres';
import ChangePassword from '../views/utilities/ChangePasswordPage';
// Forms & Dynamic Input
import inputForms from '../menu-items/inputForms';
import configData from '../config';

// Lazy Load PowerBI Report Page (Fix)
const PowerBiReportPage = Loadable(lazy(() => import('../views/utilities/SubPages/PowerBiReportPage')));



// Lazy Pages
const LoginPage = lazy(() => import('../views/pages/authentication/login'));
const ChatWidgetPage = lazy(() => import('../agents/chatwidget/ChatWidgetPage'));
const SpreadsheetAIPage = lazy(() => import('../agents/spreadsheetai/SpreadsheetAIPage'));
const DocumentAgentPage = lazy(() => import('../agents/documentagent/DocumentAgentPage'));
const TaxAgentPage = lazy(() => import('../agents/taxagent/TaxAgentPage'));
const LabourLawAdvisorPage = lazy(() => import('../LabourLawAgent/LabourLawAgent'));

// Common Forms
const routeComponents = {
  Pricing: Loadable(lazy(() => import('../views/forms/common/Pricing'))),
  Invoicebookingform: Loadable(lazy(() => import('../views/forms/common/Invoicebookingform'))),
  PartMasterUpload: Loadable(lazy(() => import('../views/forms/common/PartMasterUpload'))),
  MaterialIssueForm: Loadable(lazy(() => import('../views/forms/common/MaterialIssueForm'))),
};

const ProtectedRoute = ({ component: Component, roles, ...rest }) => {
  // Try all paths the role string might live at
  const userRole = useSelector((state) => {
    const a = state?.account || {};
    return (
      a.user?.role?.role ||
      a.user?.role_name ||
      (typeof a.user?.role === 'string' ? a.user.role : '') ||
      a.org_member?.role?.role ||
      a.org_member?.role_name ||
      (typeof a.org_member?.role === 'string' ? a.org_member.role : '') ||
      a.role ||
      ''
    );
  });
  return (
    <Route
      {...rest}
      render={(props) =>
        roles.includes(userRole) ? <Component {...props} /> : <Redirect to="/access-denied" />
      }
    />
  );
};

const USER_TYPE = {
    OWNER: 'Owner',
    ADMIN: 'Admin',
    SUPERADMIN: 'Super Admin',
    DEPARTMENTHEAD: 'Department Head',
    STAFFMEMBER: 'Staff Member'
};

const MainRoutes = () => {
  const location = useLocation();
  const history  = useHistory();
  const dispatch = useDispatch();
  const account = useSelector((state) => state.account);
  
  const isLoggedIn = account.isLoggedIn;
  const orgMember = account.org_member || {};
  const orgId = account.org?.id || account.org_data?.data?.id;
  // Resolve role from all possible paths the API might return it at
  const userRole = (
    account.user?.role?.role ||
    account.user?.role_name ||
    (typeof account.user?.role === 'string' ? account.user.role : '') ||
    account.org_member?.role?.role ||
    account.org_member?.role_name ||
    (typeof account.org_member?.role === 'string' ? account.org_member.role : '') ||
    account.role ||
    ''
  );
  const isOwner = userRole === 'Owner';
  const forms = orgId ? inputForms(orgId) : inputForms('common');

  // Owners always get ImageI access regardless of license flag
  const hasImageI = isOwner || orgMember.license_allocated === true;
  const hasChatWidget = orgMember.license_allocated_chatwidget === true;
  const hasSpreadsheetAI = orgMember.license_allocated_spreadsheet_ai === true;
  const hasDocumentAgent = orgMember.license_allocated_document_agent === true;
  const hasTaxAgent = orgMember.license_allocated_tax_agent === true;
  const hasLabourLawAdvisor = orgMember.license_allocated_labour_law_advisor === true;
  const ReportSnapshotView = lazy(() => import('../insight-hub/pages/report-snapshot-view').then((module) => ({ default: module.ReportSnapshotView })));

  const getDefaultRedirect = () => {
    if (hasImageI) return '/imagei/overview';
    if (hasChatWidget) return '/chat-agent';
    if (hasSpreadsheetAI) return '/spreadsheet-agent';
    if (hasDocumentAgent) return '/document-agent';
    if (hasTaxAgent) return '/tax-agent';
    if (hasLabourLawAdvisor) return '/labour-law';
    return '/access-denied';
  };
  React.useEffect(() => {
    console.log("🔹 Labour Law Redirect Check Triggered");
    console.log("isLoggedIn:", isLoggedIn);
    console.log("hasLabourLawAdvisor:", hasLabourLawAdvisor);
    console.log("hasImageI:", hasImageI);
    const onLabourLawPage = location.pathname === "/labour-law";
    const email = account?.user?.email || "";
    const name = account?.user?.first_name

    if (isLoggedIn && hasLabourLawAdvisor) {
      const token = localStorage.getItem("token"); // IMAGE-I JWT

      fetch(`http://ec2-43-205-136-189.ap-south-1.compute.amazonaws.com/labour_law_agent/verify_or_create_user/`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ 
          imagei_token: token,
          email,
          name,
        }),
      })
        .then((res) => res.json())
        .then((data) => {
          if (data?.access_token) {
            localStorage.setItem("labour_law_token", data.access_token);
            // redirect to S3 or custom domain
            //window.location.href = `http://localhost:8000?token=${data.labourlaw_token}`;
          } else {
            alert("Authentication failed for Labour Law Agent");
          }
        })
        .catch((err) => console.error("LLAuth Error:", err));
    }
  }, [isLoggedIn, hasLabourLawAdvisor]);
  
  return (
    <Switch>
      <Route exact path="/login">
        <Suspense fallback={<div>Loading...</div>}>
          <LoginPage />
        </Suspense>
      </Route>
      <Route exact path="/report-snapshot-view">
        <Suspense fallback={<div>Loading...</div>}>
          <ReportSnapshotView />
        </Suspense>
      </Route>

      {/* ── Management Reporting — has its own sidebar/topbar, NOT wrapped in MainLayout */}
      <Route path="/imagei">
        {!isLoggedIn ? (
          <Redirect to="/login" />
        ) : (
          <InsightHubLayout
            user={account.user ? {
              name: `${account.user.first_name || ''} ${account.user.last_name || ''}`.trim() || account.user.email,
              email: account.user.email,
              role: account.user?.role?.role || 'user',
              initials: ((account.user.first_name || '')[0] || '') + ((account.user.last_name || '')[0] || '') || (account.user.email || 'U')[0].toUpperCase(),
            } : null}
            onLogout={() => performLogout(dispatch, history)}
          />
        )}
      </Route>
      <Route>
        {!isLoggedIn ? (
          <Redirect to="/login" />
        ) : (
          <InsightHubShell
            user={account.user ? {
              name: `${account.user.first_name || ''} ${account.user.last_name || ''}`.trim() || account.user.email,
              email: account.user.email,
              role: account.user?.role?.role || 'user',
              initials: (((account.user.first_name || '')[0] || '') + ((account.user.last_name || '')[0] || '')) || (account.user.email || 'U')[0].toUpperCase(),
            } : null}
            onLogout={() => performLogout(dispatch, history)}
          >
            <Suspense fallback={<div style={{ padding: 40, textAlign: 'center', color: 'var(--muted-foreground)' }}>Loading…</div>}>
            <Switch location={location} key={location.pathname}>
              <Route path="/forgot-password" component={ForgotPasswordPage} />
              <Route path="/change-password" component={ChangePassword} />

              {(hasImageI || userRole === 'Owner') && (
                <>
                  <Route exact path="/dashboard1" component={Dashboard1} />
                  <Route exact path="/dashboard" component={DashboardDefault} />
                  <ProtectedRoute path="/dashboard-report-allocation-page" component={DashboardReportAllocationPage} roles={['Super Admin','Admin']} />
                  <ProtectedRoute path="/org-stats" component={OrgStats} roles={['Owner']} />
                  <ProtectedRoute path="/chart-of-accounts" component={ChartOfAccounts} roles={['Admin','Super Admin']} />
                  <ProtectedRoute path="/trail-balance" component={CummlativeKmRun} roles={['Admin','Super Admin']} />
                  <ProtectedRoute path="/master" component={Master} roles={['Super Admin','Admin','Department Head']} />
                  <ProtectedRoute path="/organization-profile" component={CasingDamageReport} roles={['Super Admin']} />
                  <ProtectedRoute path="/manage-users" component={ManageUsers} roles={['Super Admin','Admin','Department Head']} />
                  <ProtectedRoute path="/manage-subscriptions" component={ManageSubscriptions} roles={['Owner']} />
                  <ProtectedRoute path="/access-manage-reports" component={AccessAndManageReports} roles={['Owner']} />
                  <Route path="/reports-allocation" component={ReportsAllocation} />
                  <Route path="/power-bi" component={PowerBI} />
                  <Route path="/power-bi-report-page" component={PowerBiReportPage} />
                  <Route path="/bill-of-materials" component={BillOfMaterial} />
                  <Route path="/material-master-upload" component={MaterialMasterUpload} />
                  <Route path="/activity" component={PassiveTyres} />
                  <Route path="/upload_file" component={UploadFile} />
                  <Route path="/dashboard-reports" component={Reports} />
                  <Route path="/settings" component={SettingsPage} />
                  <Route path="/profile" component={UserProfile} />
                  <Route path="/data-management" component={DataManagement} />
                  <Route path="/manage-excel-upload" component={ManageExcelUpload} />
                  <Route path="/manage-records" component={ManageRecords} />
                  <Route path="/report-design" component={ReportDesign} />
                  {Object.entries(routeComponents).map(([name, Comp]) => (
                    <Route key={name} path={`/${name.toLowerCase()}`} component={Comp} />
                  ))}
                  {forms?.children?.map((form) => (
                    <Route key={form.url} exact path={form.url} component={form.component} />
                  ))}
                </>
              )}

              {hasChatWidget && <Route exact path="/chat-agent" component={ChatWidgetPage} />}
              {hasSpreadsheetAI && <Route exact path="/spreadsheet-agent" component={SpreadsheetAIPage} />}
              {hasDocumentAgent && <Route exact path="/document-agent" component={DocumentAgentPage} />}
              {hasTaxAgent && <Route exact path="/tax-agent" component={TaxAgentPage} />}
              {hasLabourLawAdvisor && <Route exact path="/labour-law" component={LabourLawAdvisorPage} />}
              <Route path="/access-denied" component={AccessDenied} />
              <Redirect to={getDefaultRedirect()} />
            </Switch>
            </Suspense>
          </InsightHubShell>
        )}
      </Route>
    </Switch>
  );
};

export default MainRoutes